import React, { useState, useEffect, useCallback } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { VideoMetadata, getProfile, getAllVideos } from '../lib/metadata';
import { getForYouFeed, getTrendingFeed, recordSignal } from '../lib/signals';
import { getFeedPosts, getThread, Post } from '../lib/posts';
import { getEventFeedListings, getRSVPCount, getUserRSVP, publishRSVP, EventListing } from '../lib/eventListings';
import { getAllCommunities, joinCommunity, isMember, Community } from '../lib/communities';
import { loadOrCreateIdentity } from '../lib/identity';
import { asText, linkifyText, pubkeyShortHash, parseMagnetURI } from '../lib/sanitize';
import { getLikeCount, hasLiked, addLikesListener } from '../lib/likes';
import { getFile } from '../lib/fileStorage';
import { useVideoStream } from '../lib/torrent';
import { buildVideoLink, buildPostLink } from '../lib/deepLink';
import { publishEvent } from '../lib/sync';
import { Modal } from './Modal';
import { Comments } from './Comments';
import { PostCard } from './PostCard';
import { PostComposer } from './PostComposer';

interface Props {
  profileCreated: boolean;
  onViewVideo: (hash: string) => void;
  onNavigate: (view: 'posts' | 'events' | 'communities' | 'live') => void;
  onRequireAccount: () => void;
}

function getFollowedUserIds(userId: string): Set<string> {
  try {
    const follows = JSON.parse(localStorage.getItem('authenticulture_follows') ?? '[]') as Array<{
      followerUserId: string;
      followeeUserId: string;
    }>;
    return new Set(follows.filter(f => f.followerUserId === userId).map(f => f.followeeUserId));
  } catch {
    return new Set();
  }
}

type Identity = ReturnType<typeof loadOrCreateIdentity>;

type FeedItem =
  | { kind: 'video'; ts: number; score: number; data: VideoMetadata }
  | { kind: 'post'; ts: number; score: number; data: Post }
  | { kind: 'event'; ts: number; score: number; data: EventListing }
  | { kind: 'community'; ts: number; score: number; data: Community };

const cardStyle: React.CSSProperties = {
  flex: '0 0 100%',
  height: '100%',
  scrollSnapAlign: 'start',
  scrollSnapStop: 'always',
  position: 'relative',
  overflow: 'hidden',
  background: COLORS.bg,
};

const SHADE_GRADIENT =
  'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 45%), ' +
  'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 18%)';

const METAL_GRADIENT =
  'radial-gradient(circle at 32% 26%, rgba(255,255,255,0.14), rgba(255,255,255,0) 42%), ' +
  'linear-gradient(180deg, #5c5c58 0%, #3a3a38 36%, #232220 52%, #34322f 68%, #4a4844 100%)';
const METAL_SHADOW = 'inset 0 1px 1px rgba(255,255,255,0.18), inset 0 -2px 4px rgba(0,0,0,0.65), 0 3px 6px rgba(0,0,0,0.6)';
const METAL_SHADOW_PRESSED = 'inset 0 1px 1px rgba(255,255,255,0.1), inset 0 -1px 2px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.6)';

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

function formatEventDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatEventTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function RailButton({ icon, label, active, onClick }: { icon: string; label?: string; active?: boolean; onClick: (e: React.MouseEvent) => void }) {
  const restShadow = active ? `${METAL_SHADOW}, 0 0 12px ${COLORS.accent}` : METAL_SHADOW;
  const pressedShadow = active ? `${METAL_SHADOW_PRESSED}, 0 0 12px ${COLORS.accent}` : METAL_SHADOW_PRESSED;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        background: METAL_GRADIENT,
        border: '1px solid #0a0a09',
        boxShadow: restShadow,
        color: active ? COLORS.accent : COLORS.highlight,
        fontSize: '18px',
        fontFamily: '"IBM Plex Mono", monospace',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease, transform 0.1s ease',
      }}
      onMouseDown={e => { e.currentTarget.style.boxShadow = pressedShadow; e.currentTarget.style.transform = 'translateY(1px)'; }}
      onMouseUp={e => { e.currentTarget.style.boxShadow = restShadow; e.currentTarget.style.transform = 'none'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = restShadow; e.currentTarget.style.transform = 'none'; }}
    >
      <span style={{ filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.6))' }}>{icon}</span>
      {label && <span style={{ fontSize: '8px', color: COLORS.text, textShadow: '0 1px 0 rgba(0,0,0,0.6)', letterSpacing: '0.04em' }}>{label}</span>}
    </button>
  );
}

function LedDot({ color = COLORS.highlight, size = 7 }: { color?: string; size?: number }) {
  return (
    <span style={{
      display: 'inline-block',
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      flexShrink: 0,
      background: `radial-gradient(circle at 35% 30%, #fff 0%, ${color} 45%, #111 130%)`,
      boxShadow: `inset 0 0 2px rgba(0,0,0,0.7), 0 0 6px ${color}`,
      border: '1px solid rgba(0,0,0,0.6)',
    }} />
  );
}

function FeedCardShell({ background, overlay, rail, onClick }: { background: React.ReactNode; overlay?: React.ReactNode; rail?: React.ReactNode; onClick?: () => void }) {
  return (
    <div style={{ ...cardStyle, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      {background}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: SHADE_GRADIENT }} />
      {overlay && (
        <div
          style={{
            position: 'absolute',
            left: SPACING.lg,
            right: '76px',
            bottom: SPACING.xl,
            padding: SPACING.md,
            background: 'rgba(20,20,20,0.8)',
            border: `1px outset ${COLORS.border}`,
            fontFamily: '"IBM Plex Mono", monospace',
            pointerEvents: 'none',
          }}
        >
          {overlay}
        </div>
      )}
      {rail && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: SPACING.md,
            bottom: SPACING.xl,
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.lg,
            alignItems: 'center',
            zIndex: 5,
          }}
        >
          {rail}
        </div>
      )}
    </div>
  );
}

function VideoCard({ video, profileCreated, identity, onViewVideo, onRequireAccount }: {
  video: VideoMetadata;
  profileCreated: boolean;
  identity: Identity;
  onViewVideo: (hash: string) => void;
  onRequireAccount: () => void;
}) {
  const { videoRef, videoUrl, streamingViaP2P, downloadProgress } = useVideoStream(video.hash);
  const [liked, setLiked] = useState(() => hasLiked(video.hash, identity.userId));
  const [likeCount, setLikeCount] = useState(() => getLikeCount(video.hash));
  const [shared, setShared] = useState(false);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => addLikesListener(() => {
    setLikeCount(getLikeCount(video.hash));
    setLiked(hasLiked(video.hash, identity.userId));
  }), [video.hash, identity.userId]);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profileCreated) { onRequireAccount(); return; }
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    publishEvent('like', { targetId: video.hash, targetType: 'video', userId: identity.userId });
    recordSignal(video.hash, 'like', video.magnetURI);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(buildVideoLink(video.hash)).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowComments(true);
  };

  return (
    <>
    <FeedCardShell
      onClick={() => onViewVideo(video.hash)}
      background={
        videoUrl ? (
          <video src={videoUrl} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={e => e.stopPropagation()} />
        ) : streamingViaP2P ? (
          <>
            <video ref={videoRef} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={e => e.stopPropagation()} />
            {downloadProgress !== null && downloadProgress < 100 && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
                display: 'flex', alignItems: 'center', gap: SPACING.sm,
                padding: `${SPACING.xs} ${SPACING.sm}`,
                background: 'linear-gradient(180deg, #0c1115, #070a0d 60%, #050709)',
                borderBottom: '1px solid #000',
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '10px',
                color: COLORS.highlight,
                textShadow: `0 0 6px ${COLORS.highlight}80`,
                letterSpacing: '0.06em',
              }}>
                <LedDot color={COLORS.highlight} />
                <span style={{ whiteSpace: 'nowrap' }}>peers · {downloadProgress}%</span>
                <div style={{ flex: 1, height: '4px', background: '#000', borderRadius: '2px', overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8)' }}>
                  <div style={{ width: `${downloadProgress}%`, height: '100%', background: COLORS.highlight, boxShadow: `0 0 6px ${COLORS.highlight}`, transition: 'width 0.3s ease' }} />
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace' }}>
            video not available
          </div>
        )
      }
      overlay={
        <>
          <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: SPACING.xs, fontSize: '13px' }}>{asText(video.title)}</div>
          <div style={{ fontSize: '11px', color: COLORS.textMuted }}>
            {pubkeyShortHash(video.uploader)}{video.tags.length > 0 ? ` · ${video.tags.slice(0, 3).join(', ')}` : ''}
          </div>
        </>
      }
      rail={
        <>
          <RailButton icon="♥" label={likeCount > 0 ? String(likeCount) : 'like'} active={liked} onClick={handleLike} />
          <RailButton icon="↩" label="comment" onClick={handleComment} />
          <RailButton icon={shared ? '✓' : '⇥'} label={shared ? 'copied' : 'share'} active={shared} onClick={handleShare} />
        </>
      }
    />
    {showComments && (
      <Modal title="comments" onClose={() => setShowComments(false)}>
        <Comments videoHash={video.hash} />
      </Modal>
    )}
    </>
  );
}

function PostFeedCard({ post, profileCreated, identity, onNavigate, onRequireAccount }: {
  post: Post;
  profileCreated: boolean;
  identity: Identity;
  onNavigate: (view: 'posts' | 'events' | 'communities') => void;
  onRequireAccount: () => void;
}) {
  const [liked, setLiked] = useState(() => hasLiked(post.id, identity.userId));
  const [likeCount, setLikeCount] = useState(() => getLikeCount(post.id));
  const [shared, setShared] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [thread, setThread] = useState<Post[]>([post]);

  useEffect(() => addLikesListener(() => {
    setLikeCount(getLikeCount(post.id));
    setLiked(hasLiked(post.id, identity.userId));
  }), [post.id, identity.userId]);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profileCreated) { onRequireAccount(); return; }
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    recordSignal(post.id, 'like', post.magnetURI);
    publishEvent('like', { targetId: post.id, targetType: 'post', userId: identity.userId });
  };

  const refreshThread = () => setThread(getThread(post.id));

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    refreshThread();
    setShowThread(true);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    recordSignal(post.id, 'share', post.magnetURI);
    navigator.clipboard?.writeText(buildPostLink(post.id)).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  const content = linkifyText(asText(post.content));

  return (
    <>
    <FeedCardShell
      onClick={() => onNavigate('posts')}
      background={
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING.xxl, background: COLORS.panel }}>
          <div style={{ fontSize: '20px', lineHeight: 1.6, color: COLORS.text, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'center', wordBreak: 'break-word', maxWidth: '520px' }}>
            {content.map((seg, i) => seg.type === 'url'
              ? <a key={i} href={seg.content} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: COLORS.highlight }}>{seg.content}</a>
              : <span key={i}>{seg.content}</span>)}
          </div>
        </div>
      }
      overlay={
        <>
          <div style={{ fontWeight: 700, color: COLORS.highlight, marginBottom: SPACING.xs, fontSize: '13px' }}>{pubkeyShortHash(post.pubkey)}</div>
          <div style={{ fontSize: '11px', color: COLORS.textMuted }}>{timeAgo(post.timestamp)}{post.city ? ` · ${post.city}` : ''}</div>
        </>
      }
      rail={
        <>
          <RailButton icon="♥" label={likeCount > 0 ? String(likeCount) : 'like'} active={liked} onClick={handleLike} />
          <RailButton icon="↩" label="comment" onClick={handleComment} />
          <RailButton icon={shared ? '✓' : '⇥'} label={shared ? 'copied' : 'share'} active={shared} onClick={handleShare} />
        </>
      }
    />
    {showThread && (
      <Modal title="comments" onClose={() => setShowThread(false)}>
        <PostCard post={post} allPosts={thread} depth={0} onReloadFeed={refreshThread} />
        <PostComposer replyTo={{ pubkey: post.pubkey, postId: post.id }} onPosted={refreshThread} />
      </Modal>
    )}
    </>
  );
}

function EventFeedCard({ listing, profileCreated, identity, onNavigate, onRequireAccount }: {
  listing: EventListing;
  profileCreated: boolean;
  identity: Identity;
  onNavigate: (view: 'posts' | 'events' | 'communities') => void;
  onRequireAccount: () => void;
}) {
  const [flyerUrl, setFlyerUrl] = useState<string | null>(null);
  const [shared, setShared] = useState(false);
  const counts = getRSVPCount(listing.id);
  const myRSVP = getUserRSVP(listing.id, identity.userId);

  useEffect(() => {
    if (!listing.flyerMagnetURI || listing.flyerType !== 'image') return;
    const parsed = parseMagnetURI(listing.flyerMagnetURI);
    if (!parsed) return;
    let url: string | null = null;
    getFile(parsed.infoHash).then(blob => {
      if (blob) {
        url = URL.createObjectURL(blob);
        setFlyerUrl(url);
      }
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [listing.flyerMagnetURI, listing.flyerType]);

  const handleRSVP = async (e: React.MouseEvent, status: 'going' | 'interested') => {
    e.stopPropagation();
    if (!profileCreated) { onRequireAccount(); return; }
    await publishRSVP(listing.id, status);
    recordSignal(listing.id, status === 'going' ? 'pin' : 'save', listing.flyerMagnetURI);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    recordSignal(listing.id, 'share', listing.flyerMagnetURI);
    navigator.clipboard?.writeText(`auth:event:${listing.id}`).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  return (
    <FeedCardShell
      onClick={() => onNavigate('events')}
      background={
        flyerUrl ? (
          <img src={flyerUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.panel, padding: SPACING.xxl }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.text, fontFamily: '"IBM Plex Mono", monospace', textAlign: 'center' }}>
              {asText(listing.title)}
            </div>
          </div>
        )
      }
      overlay={
        <>
          <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: SPACING.xs, fontSize: '13px' }}>{asText(listing.title)}</div>
          <div style={{ fontSize: '11px', color: COLORS.highlight, marginBottom: SPACING.xs }}>
            {formatEventDate(listing.startTime)} · {formatEventTime(listing.startTime)}
          </div>
          <div style={{ fontSize: '11px', color: COLORS.textMuted }}>
            {listing.venue ? `${asText(listing.venue)} · ` : ''}{asText(listing.city)}
          </div>
        </>
      }
      rail={
        <>
          <RailButton icon="✓" label={counts.going > 0 ? String(counts.going) : 'going'} active={myRSVP === 'going'} onClick={e => handleRSVP(e, 'going')} />
          <RailButton icon="?" label={counts.interested > 0 ? String(counts.interested) : 'rsvp'} active={myRSVP === 'interested'} onClick={e => handleRSVP(e, 'interested')} />
          <RailButton icon={shared ? '✓' : '⇥'} label={shared ? 'copied' : 'share'} active={shared} onClick={handleShare} />
        </>
      }
    />
  );
}

function CommunityFeedCard({ community, profileCreated, identity, onNavigate, onRequireAccount }: {
  community: Community;
  profileCreated: boolean;
  identity: Identity;
  onNavigate: (view: 'posts' | 'events' | 'communities') => void;
  onRequireAccount: () => void;
}) {
  const [joined, setJoined] = useState(() => isMember(community.id, identity.userId));

  const handleJoin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profileCreated) { onRequireAccount(); return; }
    if (joined) return;
    const username = getProfile()?.username || pubkeyShortHash(identity.publicKey);
    joinCommunity(community.id, identity.userId, username);
    setJoined(true);
  };

  return (
    <FeedCardShell
      onClick={() => onNavigate('communities')}
      background={
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: SPACING.xxl, background: COLORS.panel, textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, color: COLORS.accent, marginBottom: SPACING.md, fontFamily: '"IBM Plex Mono", monospace' }}>
            {asText(community.name)}
          </div>
          <div style={{ fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6, maxWidth: '480px', fontFamily: '"IBM Plex Mono", monospace' }}>
            {asText(community.description)}
          </div>
        </div>
      }
      overlay={
        <>
          <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: SPACING.xs, fontSize: '13px' }}>{asText(community.name)}</div>
          <div style={{ fontSize: '11px', color: COLORS.textMuted }}>{community.memberCount} member{community.memberCount === 1 ? '' : 's'}</div>
        </>
      }
      rail={
        <RailButton icon={joined ? '✓' : '+'} label={joined ? 'joined' : 'join'} active={joined} onClick={handleJoin} />
      }
    />
  );
}

function FeedTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? `2px solid ${COLORS.accent}` : '2px solid transparent',
        color: active ? COLORS.highlight : COLORS.textMuted,
        cursor: 'pointer',
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '12px',
        fontWeight: active ? 700 : 400,
        letterSpacing: '0.05em',
        padding: `${SPACING.xs} ${SPACING.sm}`,
      }}
    >
      {label}
    </button>
  );
}

export function DiscoverFeed({ profileCreated, onViewVideo, onNavigate, onRequireAccount }: Props) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [mode, setMode] = useState<'foryou' | 'following'>('foryou');
  const identity = loadOrCreateIdentity();

  const load = useCallback(() => {
    if (mode === 'following') {
      const followed = getFollowedUserIds(identity.userId);
      const videos: FeedItem[] = getAllVideos()
        .filter(v => followed.has(v.uploader))
        .map(v => ({ kind: 'video' as const, ts: v.uploadedAt, score: v.uploadedAt, data: v }));
      const posts: FeedItem[] = getFeedPosts('following', identity.userId, 30)
        .map(p => ({ kind: 'post' as const, ts: p.timestamp, score: p.timestamp, data: p }));
      setItems([...videos, ...posts].sort((a, b) => b.ts - a.ts).slice(0, 60));
      return;
    }

    const videoSource = profileCreated ? getForYouFeed(20) : getTrendingFeed(20);
    const videos: FeedItem[] = videoSource.map(v => ({
      kind: 'video' as const,
      ts: v.uploadedAt,
      score: (v as { algorithmScore?: number }).algorithmScore ?? 0,
      data: v,
    }));
    const posts: FeedItem[] = getFeedPosts(profileCreated ? 'foryou' : 'trending', identity.userId, 20)
      .map((p, i) => ({ kind: 'post' as const, ts: p.timestamp, score: 1 / (i + 1), data: p }));
    const events: FeedItem[] = getEventFeedListings('soon', { limit: 10 })
      .map(e => ({ kind: 'event' as const, ts: e.startTime, score: 0, data: e }));
    const communities: FeedItem[] = getAllCommunities()
      .sort((a, b) => b.memberCount - a.memberCount)
      .slice(0, 10)
      .map(c => ({ kind: 'community' as const, ts: c.createdAt, score: 0, data: c }));

    setItems(
      [...videos, ...posts, ...events, ...communities]
        .sort((a, b) => b.score - a.score)
        .slice(0, 60)
    );
  }, [mode, profileCreated, identity.userId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sm,
        padding: `${SPACING.xs} ${SPACING.sm}`,
        flexShrink: 0,
        borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.panel,
      }}>
        {profileCreated ? (
          <div style={{ display: 'flex', gap: SPACING.xs }}>
            <FeedTab label="for you" active={mode === 'foryou'} onClick={() => setMode('foryou')} />
            <FeedTab label="following" active={mode === 'following'} onClick={() => setMode('following')} />
          </div>
        ) : (
          <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', fontWeight: 700, color: COLORS.accent, letterSpacing: '0.05em', paddingLeft: SPACING.xs }}>
            discover
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => onNavigate('live')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: `1px solid ${COLORS.border}`,
            color: COLORS.text,
            cursor: 'pointer',
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '10px',
            fontWeight: 700,
            padding: `${SPACING.xs} ${SPACING.sm}`,
          }}
        >
          <span style={{ color: COLORS.accent }}>◉</span> live
        </button>
        {!profileCreated && (
          <button
            onClick={onRequireAccount}
            style={{
              background: COLORS.accent,
              color: '#fff',
              border: `2px outset ${COLORS.highlight}`,
              padding: `${SPACING.xs} ${SPACING.sm}`,
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '10px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            sign up
          </button>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', width: '100%' }}>
        {items.length === 0 && (
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: SPACING.xl, textAlign: 'center', fontFamily: '"IBM Plex Mono", monospace' }}>
            <p style={{ fontSize: '14px', fontWeight: 700, color: COLORS.accent, marginBottom: SPACING.md }}>
              welcome to authenticulture.
            </p>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: SPACING.sm, lineHeight: 1.6, maxWidth: '420px' }}>
              no ads. no algorithm you didn't build. no one selling your data.
            </p>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, lineHeight: 1.6, maxWidth: '420px' }}>
              your feed fills up as people on the network post, upload, and share.
            </p>
          </div>
        )}
        {items.map(item => {
          if (item.kind === 'video') {
            return (
              <VideoCard
                key={`v-${item.data.hash}`}
                video={item.data}
                profileCreated={profileCreated}
                identity={identity}
                onViewVideo={onViewVideo}
                onRequireAccount={onRequireAccount}
              />
            );
          }
          if (item.kind === 'post') {
            return (
              <PostFeedCard
                key={`p-${item.data.id}`}
                post={item.data}
                profileCreated={profileCreated}
                identity={identity}
                onNavigate={onNavigate}
                onRequireAccount={onRequireAccount}
              />
            );
          }
          if (item.kind === 'event') {
            return (
              <EventFeedCard
                key={`e-${item.data.id}`}
                listing={item.data}
                profileCreated={profileCreated}
                identity={identity}
                onNavigate={onNavigate}
                onRequireAccount={onRequireAccount}
              />
            );
          }
          return (
            <CommunityFeedCard
              key={`c-${item.data.id}`}
              community={item.data}
              profileCreated={profileCreated}
              identity={identity}
              onNavigate={onNavigate}
              onRequireAccount={onRequireAccount}
            />
          );
        })}
      </div>
    </div>
  );
}
